/**
 * Patient contact context for SendQuestionnaireDialog's channel gating
 * (Plan 2, Task 4).
 *
 * AssessmentsPanel only receives a patientId prop (App.tsx) — there is no
 * single-patient GET endpoint and none is added here (spec explicitly says
 * do not add a new get_patient route). Instead this reuses GET /patients,
 * the same list endpoint PatientSelector already calls, and finds the row
 * by id. get_patients.py returns raw DynamoDB items with no output
 * whitelist/projection, so `email` and `client_status` (and `client_sub`)
 * are already present whenever they exist on the record — verified by
 * reading the lambda; no backend change was needed for this hook.
 *
 * Loud errors: a failed fetch clears the contact and surfaces `error`
 * rather than pretending gating can proceed on stale/absent data.
 */
import { useCallback, useEffect, useState } from "react";
import { API_BASE } from "../../config";
import { useAuth } from "../../auth/AuthProvider";

export type PatientContact = {
  email?: string;
  clientStatus?: string;
  clientSub?: string;
};

export function usePatientContact(patientId: string) {
  const { ensureToken } = useAuth();
  const [contact, setContact] = useState<PatientContact | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    if (!patientId?.trim()) {
      setContact(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const token = await ensureToken();
      const resp = await fetch(`${API_BASE}/patients`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      });

      const text = await resp.text();
      let data: any = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        throw new Error(`HTTP ${resp.status} (non-JSON response)`);
      }

      if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`);

      const items: any[] = Array.isArray(data?.items) ? data.items : [];
      const row = items.find((p) => p.patient_id === patientId.trim());
      setContact(row ? {
        email: row.email ? String(row.email).trim() : undefined,
        clientStatus: row.client_status ? String(row.client_status) : undefined,
        clientSub: row.client_sub ? String(row.client_sub) : undefined,
      } : null);
    } catch (e: any) {
      if (signal?.aborted) return;
      console.error("[usePatientContact] load:", e);
      setError(e?.message ? String(e.message) : "Failed to load patient contact info");
      setContact(null);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [patientId, ensureToken]);

  useEffect(() => {
    const ac = new AbortController();
    void load(ac.signal);
    return () => ac.abort();
  }, [load]);

  return { contact, loading, error, reload: load };
}
